import type { GeoJSON, LatLng, LayerGroup, Map, Point } from "leaflet"
import type { DataSetter } from "../utils/mapLayers"
import { getBearing } from "../utils/utils"
import {
	getWorldCopyLongitudeOffsets,
	getWorldCopyPoints,
	getWorldCopyTrackSegments,
} from "./vesselTrackMapEdges"

const MIN_TRACK_POINTS = 2

type EndpointMarkerData = {
	id: string
	vesselId: number
	vesselName: string
	color: string
	point: {
		timestamp: Date
		latitude: number
		longitude: number
	}
}

type EndpointLayerState = {
	endpointLayer: LayerGroup
	map?: Map
	update: () => void
}

const endpointLayerByGroup = new WeakMap<LayerGroup, EndpointLayerState>()

const isLeafletLayerGroup = (value: LayerGroup | GeoJSON | undefined): value is LayerGroup => {
	return value !== undefined && "clearLayers" in value && "addLayer" in value
}

const escapeHtml = (value: string) =>
	value
		.replaceAll("&", "&amp;")
		.replaceAll("<", "&lt;")
		.replaceAll(">", "&gt;")
		.replaceAll('"', "&quot;")
		.replaceAll("'", "&#39;")

const ENDPOINT_BADGE_HEIGHT = 26
const ENDPOINT_BADGE_ICON_SIZE = 20

const getEndpointBadgeMetrics = (label: string) => {
	const width = Math.max(96, Math.min(240, label.length * 6 + 52))
	const anchorX = Math.max(48, Math.min(120, label.length * 3 + 18))

	return {
		width,
		height: ENDPOINT_BADGE_HEIGHT,
		anchorX,
		anchorY: ENDPOINT_BADGE_HEIGHT / 2,
	}
}

const createEndpointPinSvg = () => {
	const iconSize = Math.round(ENDPOINT_BADGE_ICON_SIZE * 0.6)

	return `
		<svg viewBox="0 0 24 24" width="${iconSize}" height="${iconSize}" fill="currentColor" style="color:#ffffff; display:block;">
			<path fill="none" d="M0 0h24v24H0z"></path>
			<path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5a2.5 2.5 0 010-5 2.5 2.5 0 010 5z"></path>
		</svg>
	`
}

const createEndpointChevronSvg = (rotation: number) => {
	const iconSize = Math.round(ENDPOINT_BADGE_ICON_SIZE * 0.6)

	return `
		<svg viewBox="0 0 24 24" width="${iconSize}" height="${iconSize}" style="transform: rotate(${rotation}deg); transform-origin: center; display: block;">
			<path
				d="M 6 9 L 12 15 L 18 9"
				fill="none"
				stroke="#ffffff"
				stroke-width="3.8"
				stroke-linecap="round"
				stroke-linejoin="round"
			/>
		</svg>
	`
}

const createEndpointBadgeHtml = (
	label: string,
	color: string,
	iconKind: "pin" | "chevron",
	rotation: number = 0
) => {
	return `
		<div class="inline-flex min-h-6.5 items-center gap-1.5 rounded-full border border-white/90 bg-white/85 px-0.75 py-0.75 shadow">
			<span
				class="flex h-5 w-5 items-center justify-center rounded-full border border-white shadow-sm"
				style="background:${color}; color:${color};"
			>
				${iconKind === "pin" ? createEndpointPinSvg() : createEndpointChevronSvg(rotation)}
			</span>
			<span class="whitespace-nowrap text-[11px] font-semibold leading-none" style="color:${color};">
				${escapeHtml(label)}
			</span>
		</div>
	`
}

const createVisibleEndpointLabelIcon = (label: string, color: string) => {
	const metrics = getEndpointBadgeMetrics(label)

	return L.divIcon({
		className: "pointer-events-none",
		html: createEndpointBadgeHtml(label, color, "pin"),
		iconSize: [metrics.width, metrics.height],
		iconAnchor: [metrics.anchorX, metrics.anchorY],
	})
}

const createHelperEndpointIcon = (label: string, color: string, angle: number) => {
	const metrics = getEndpointBadgeMetrics(label)

	return L.divIcon({
		className: "pointer-events-none",
		html: createEndpointBadgeHtml(label, color, "chevron", angle - 180),
		iconSize: [metrics.width, metrics.height],
		iconAnchor: [metrics.anchorX, metrics.anchorY],
	})
}

const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(value, max))

type ScreenRect = {
	left: number
	top: number
	right: number
	bottom: number
	width: number
	height: number
}

const rectFromCenter = (center: Point, width: number, height: number): ScreenRect => {
	return {
		left: center.x - width / 2,
		top: center.y - height / 2,
		right: center.x + width / 2,
		bottom: center.y + height / 2,
		width,
		height,
	}
}

const rectOverlaps = (left: ScreenRect, right: ScreenRect) => {
	return (
		left.left < right.right &&
		left.right > right.left &&
		left.top < right.bottom &&
		left.bottom > right.top
	)
}

const getOverlapCount = (candidate: ScreenRect, occupiedRects: ScreenRect[]) => {
	let count = 0
	for (const rect of occupiedRects) {
		if (rectOverlaps(candidate, rect) === true) {
			count += 1
		}
	}
	return count
}

const getLabelCandidateOffsets = () => [
	L.point(24, 0),
	L.point(-24, 0),
	L.point(0, -24),
	L.point(0, 24),
	L.point(30, -20),
	L.point(30, 20),
	L.point(-30, -20),
	L.point(-30, 20),
	L.point(42, 0),
	L.point(-42, 0),
	L.point(0, -36),
	L.point(0, 36),
]

const getHelperContainerPoint = (map: Map, point: LatLng) => {
	const size = map.getSize()
	const containerPoint = map.latLngToContainerPoint(point)
	const center = size.divideBy(2)
	const padding = 24
	const minX = padding
	const minY = padding
	const maxX = size.x - padding
	const maxY = size.y - padding
	const dx = containerPoint.x - center.x
	const dy = containerPoint.y - center.y

	if (dx === 0 && dy === 0) {
		return containerPoint
	}

	const tCandidates: number[] = []
	if (dx > 0) {
		tCandidates.push((maxX - center.x) / dx)
	} else if (dx < 0) {
		tCandidates.push((minX - center.x) / dx)
	}
	if (dy > 0) {
		tCandidates.push((maxY - center.y) / dy)
	} else if (dy < 0) {
		tCandidates.push((minY - center.y) / dy)
	}

	const t = tCandidates.filter((candidate) => candidate > 0).sort((a, b) => a - b)[0]
	if (t === undefined) {
		return containerPoint
	}

	const edgePoint = L.point(center.x + dx * t, center.y + dy * t)
	edgePoint.x = clamp(edgePoint.x, minX, maxX)
	edgePoint.y = clamp(edgePoint.y, minY, maxY)
	return edgePoint
}

type HelperMarkerCandidate = EndpointMarkerData & {
	helperContainerPoint: Point
	targetContainerPoint: Point
}

type VisibleMarkerCandidate = EndpointMarkerData & {
	containerPoint: Point
	labelText: string
	labelColor: string
	labelWidth: number
	labelHeight: number
}

const clusterHelperMarkers = (
	candidates: HelperMarkerCandidate[],
	thresholdPx: number = 32
): HelperMarkerCandidate[][] => {
	const clusters: HelperMarkerCandidate[][] = []
	const sortedCandidates = [...candidates].sort((left, right) => {
		const xDelta = left.helperContainerPoint.x - right.helperContainerPoint.x
		return xDelta !== 0 ? xDelta : left.helperContainerPoint.y - right.helperContainerPoint.y
	})

	for (const candidate of sortedCandidates) {
		const cluster = clusters.find((clusterItems) => {
			const center = clusterItems.reduce(
				(acc, item) =>
					L.point(
						acc.x + item.helperContainerPoint.x,
						acc.y + item.helperContainerPoint.y
					),
				L.point(0, 0)
			)
			center.x /= clusterItems.length
			center.y /= clusterItems.length
			return center.distanceTo(candidate.helperContainerPoint) <= thresholdPx
		})

		if (cluster === undefined) {
			clusters.push([candidate])
			continue
		}

		cluster.push(candidate)
	}

	return clusters
}

const renderEndpointMarkers = (
	endpointLayer: LayerGroup,
	map: Map | undefined,
	endpointMarkers: EndpointMarkerData[]
) => {
	endpointLayer.clearLayers()
	if (map === undefined) {
		return
	}

	const bounds = map.getBounds()
	const longitudeOffsets = getWorldCopyLongitudeOffsets(bounds)
	const visibleMarkers: VisibleMarkerCandidate[] = []
	const helperMarkers: HelperMarkerCandidate[] = []
	for (const endpointMarker of endpointMarkers) {
		const copiedPoints = getWorldCopyPoints(endpointMarker.point, longitudeOffsets)
		const visibleCopiedPoints = copiedPoints
			.map((pointData) => L.latLng(pointData.latitude, pointData.longitude))
			.filter((point) => bounds.contains(point))

		if (visibleCopiedPoints.length > 0) {
			const labelText = endpointMarker.vesselName
			const { width: labelWidth, height: labelHeight } = getEndpointBadgeMetrics(labelText)
			for (const point of visibleCopiedPoints) {
				const containerPoint = map.latLngToContainerPoint(point)
				visibleMarkers.push({
					...endpointMarker,
					point: {
						...endpointMarker.point,
						latitude: point.lat,
						longitude: point.lng,
					},
					containerPoint,
					labelText,
					labelColor: endpointMarker.color,
					labelWidth,
					labelHeight,
				})
			}
			continue
		}

		const center = map.getSize().divideBy(2)
		const point =
			copiedPoints
				.map((pointData) => L.latLng(pointData.latitude, pointData.longitude))
				.sort(
					(left, right) =>
						map.latLngToContainerPoint(left).distanceTo(center) -
						map.latLngToContainerPoint(right).distanceTo(center)
				)[0] ?? L.latLng(endpointMarker.point.latitude, endpointMarker.point.longitude)
		const helperContainerPoint = getHelperContainerPoint(map, point)
		helperMarkers.push({
			...endpointMarker,
			point: {
				...endpointMarker.point,
				latitude: point.lat,
				longitude: point.lng,
			},
			helperContainerPoint,
			targetContainerPoint: map.latLngToContainerPoint(point),
		})
	}

	const occupiedRects: ScreenRect[] = []
	const labelCandidates = [...visibleMarkers].sort((left, right) => {
		const yDelta = left.containerPoint.y - right.containerPoint.y
		return yDelta !== 0 ? yDelta : left.containerPoint.x - right.containerPoint.x
	})
	for (const markerData of labelCandidates) {
		const candidateOffsets = getLabelCandidateOffsets()
		let chosenRect: ScreenRect | undefined
		let chosenCenter: Point | undefined
		let smallestOverlapCount = Number.POSITIVE_INFINITY
		let bestFallbackRect: ScreenRect | undefined
		let bestFallbackCenter: Point | undefined

		for (const offset of candidateOffsets) {
			const center = L.point(
				markerData.containerPoint.x + offset.x,
				markerData.containerPoint.y + offset.y
			)
			const rect = rectFromCenter(center, markerData.labelWidth, markerData.labelHeight)
			if (
				rect.left < 8 ||
				rect.top < 8 ||
				rect.right > map.getSize().x - 8 ||
				rect.bottom > map.getSize().y - 8
			) {
				const overlapCount = getOverlapCount(rect, occupiedRects)
				if (
					overlapCount < smallestOverlapCount ||
					(overlapCount === smallestOverlapCount &&
						bestFallbackCenter !== undefined &&
						center.distanceTo(markerData.containerPoint) <
							bestFallbackCenter.distanceTo(markerData.containerPoint))
				) {
					smallestOverlapCount = overlapCount
					bestFallbackRect = rect
					bestFallbackCenter = center
				}
				continue
			}

			if (occupiedRects.some((occupiedRect) => rectOverlaps(rect, occupiedRect)) === false) {
				chosenRect = rect
				chosenCenter = center
				break
			}

			const overlapCount = getOverlapCount(rect, occupiedRects)
			if (
				overlapCount < smallestOverlapCount ||
				(overlapCount === smallestOverlapCount &&
					bestFallbackCenter !== undefined &&
					center.distanceTo(markerData.containerPoint) <
						bestFallbackCenter.distanceTo(markerData.containerPoint))
			) {
				smallestOverlapCount = overlapCount
				bestFallbackRect = rect
				bestFallbackCenter = center
			}
		}

		const fallbackCenter = bestFallbackCenter ?? markerData.containerPoint.add(L.point(24, 0))
		const finalCenter = chosenCenter ?? fallbackCenter
		const finalRect =
			chosenRect ?? rectFromCenter(finalCenter, markerData.labelWidth, markerData.labelHeight)
		occupiedRects.push(finalRect)

		endpointLayer.addLayer(
			L.marker(map.containerPointToLatLng(finalCenter), {
				icon: createVisibleEndpointLabelIcon(markerData.labelText, markerData.labelColor),
				interactive: false,
				pane: "vesselsTrackPane",
				zIndexOffset: 9500,
			})
		)
	}

	const helperClusters = clusterHelperMarkers(helperMarkers)
	for (const cluster of helperClusters) {
		const size = map.getSize()
		const padding = 24
		const centerPoint = cluster.reduce(
			(acc, item) =>
				L.point(acc.x + item.helperContainerPoint.x, acc.y + item.helperContainerPoint.y),
			L.point(0, 0)
		)
		centerPoint.x /= cluster.length
		centerPoint.y /= cluster.length
		const targetPoint = cluster.reduce(
			(acc, item) =>
				L.point(acc.x + item.targetContainerPoint.x, acc.y + item.targetContainerPoint.y),
			L.point(0, 0)
		)
		targetPoint.x /= cluster.length
		targetPoint.y /= cluster.length
		let direction = targetPoint.subtract(centerPoint)
		const directionLength = Math.sqrt(direction.x * direction.x + direction.y * direction.y)
		if (directionLength === 0) {
			direction = L.point(0, -1)
		} else {
			direction = L.point(direction.x / directionLength, direction.y / directionLength)
		}
		const perpendicular = L.point(-direction.y, direction.x)
		const offset = cluster.length > 1 ? Math.min(12, (cluster.length - 1) * 3) : 0
		const shiftedPoint = L.point(
			clamp(centerPoint.x + perpendicular.x * offset, padding, size.x - padding),
			clamp(centerPoint.y + perpendicular.y * offset, padding, size.y - padding)
		)
		const markerPosition = map.containerPointToLatLng(shiftedPoint)
		const primaryMarker = cluster[0]
		if (primaryMarker === undefined) {
			continue
		}

		const label = cluster.length > 1 ? `${cluster.length} vessels` : primaryMarker.vesselName
		const helperColor = cluster.length > 1 ? "#6b7280" : primaryMarker.color
		endpointLayer.addLayer(
			L.marker(markerPosition, {
				icon: createHelperEndpointIcon(
					label,
					helperColor,
					getBearing(
						markerPosition,
						L.latLng(primaryMarker.point.latitude, primaryMarker.point.longitude)
					)
				),
				interactive: false,
				pane: "vesselsTrackPane",
				zIndexOffset: 12000,
			})
		)
	}
}

export const addVesselTrackLegs: DataSetter = (
	dispatch,
	queryClient,
	layerGroup,
	layerData,
	navigate,
	map
) => {
	if (isLeafletLayerGroup(layerGroup) === false) return

	const leafletLayerGroup = layerGroup
	leafletLayerGroup.clearLayers()

	const { vesselsTrackLegsData } = layerData
	if (vesselsTrackLegsData === undefined) return

	const { legs, endpointMarkers = [] } = vesselsTrackLegsData
	if (legs === undefined || legs.length === 0) {
		const existingEndpointState = endpointLayerByGroup.get(leafletLayerGroup)
		if (existingEndpointState?.map !== undefined) {
			existingEndpointState.map.off("moveend zoomend", existingEndpointState.update)
		}
		const endpointLayer =
			existingEndpointState?.endpointLayer ?? L.layerGroup([], { pane: "vesselsTrackPane" })
		leafletLayerGroup.addLayer(endpointLayer)
		const update = () => renderEndpointMarkers(endpointLayer, map, endpointMarkers)
		endpointLayerByGroup.set(leafletLayerGroup, { endpointLayer, map, update })
		if (map !== undefined) {
			map.on("moveend zoomend", update)
		}
		update()
		return
	}

	const longitudeOffsets = getWorldCopyLongitudeOffsets(map?.getBounds())
	for (const leg of legs) {
		const trackPointSegments = getWorldCopyTrackSegments(leg.points, longitudeOffsets)

		for (const trackPointSegment of trackPointSegments) {
			const trackPoints = trackPointSegment.map((pointData) =>
				L.latLng(pointData.latitude, pointData.longitude)
			)

			if (trackPoints.length < MIN_TRACK_POINTS) {
				continue
			}

			leafletLayerGroup.addLayer(
				L.polyline(trackPoints, {
					color: leg.color,
					pane: "vesselsTrackPane",
					weight: 2,
					lineCap: "round",
					className: "z-1",
				})
			)
		}

		// if (map !== undefined && map.getZoom() >= 10) {
		// 	const arrowDistance =
		// 		map.getZoom() >= 13
		// 			? 500
		// 			: map.getZoom() === 12
		// 				? 1500
		// 				: map.getZoom() === 11
		// 					? 5000
		// 					: 10000

		// 	let accumulatedDistance = 0
		// 	let lastArrowIndex = 0
		// 	for (let i = 1; i < trackPoints.length; i++) {
		// 		const start = trackPoints[lastArrowIndex]
		// 		const end = trackPoints[i]
		// 		if (start === undefined || end === undefined) break

		// 		accumulatedDistance += map.distance(start, end)

		// 		if (accumulatedDistance >= arrowDistance) {
		// 			const angle = getBearing(start, end)
		// 			const arrowIcon = createArrowIcon(angle, leg.color, leg.color)

		// 			const arrowMarker = L.marker(end, {
		// 				icon: arrowIcon,
		// 				pane: "vesselsTrackPane",
		// 				zIndexOffset: 10000,
		// 			})
		// 			leafletLayerGroup.addLayer(arrowMarker)

		// 			lastArrowIndex = i
		// 			accumulatedDistance = 0
		// 		}
		// 	}
		// }
	}

	const existingEndpointState = endpointLayerByGroup.get(leafletLayerGroup)
	if (existingEndpointState?.map !== undefined) {
		existingEndpointState.map.off("moveend zoomend", existingEndpointState.update)
	}

	const endpointLayer =
		existingEndpointState?.endpointLayer ?? L.layerGroup([], { pane: "vesselsTrackPane" })
	leafletLayerGroup.addLayer(endpointLayer)

	const update = () => renderEndpointMarkers(endpointLayer, map, endpointMarkers)
	endpointLayerByGroup.set(leafletLayerGroup, { endpointLayer, map, update })
	if (map !== undefined) {
		map.on("moveend zoomend", update)
	}
	update()
}
